import { makeAutoObservable, observable, action } from "mobx";

import { Content } from "@/types";
import { ChatMessageI, ChatMessageStatsI, ChatStatsI } from "@/components/Playground/types";

const DEFAULT_STATS: ChatStatsI = {
  total_messages: 0,
  total_cost: 0,
  total_input_tokens: 0,
  total_latency_ms: 0,
  total_output_tokens: 0,
};

class ChatStore {
  @observable private _messages: Map<number, ChatMessageI[]> = new Map<number, ChatMessageI[]>();

  /** Per-variable-set pending request IDs (variableSetIndex → requestId) */
  @observable private _pending_request_ids: Map<number, number> = new Map<number, number>();

  /** Per-variable-set streaming message IDs (variableSetIndex → messageId) */
  @observable private _streaming_message_ids: Map<number, string> = new Map<number, string>();

  /** Per-variable-set stats (variableSetIndex → stats) */
  @observable private _stats: Map<number, ChatStatsI> = new Map<number, ChatStatsI>();

  /**
   * Mapping from individual_run_request_id → variableSetIndex.
   * Populated when a message is sent so that WebSocket handlers can
   * route incoming events to the correct variable set.
   */
  @observable private _request_to_variable_set: Map<number, number> = new Map<number, number>();

  @observable private _last_socket_activity: number | null = null;

  /**
   * Per-variable-set rehydration generation counter.
   * Bumped when messages are cleared and the hydration hook should
   * re-fetch from the server (e.g. after importing chat history from
   * a request).  useHydrateChatMessages watches this to reset its
   * hasInitialized ref.
   */
  @observable private _rehydration_generation: Map<number, number> = new Map<number, number>();

  constructor() {
    makeAutoObservable(this);
  }

  // ---------------------------------------------------------------------------
  // Messages
  // ---------------------------------------------------------------------------

  get getMessages() {
    return (variableSetIndex: number): ChatMessageI[] => this._messages.get(variableSetIndex) || [];
  }

  get hasMessages() {
    return (variableSetIndex: number) => {
      const messages = this._messages.get(variableSetIndex);
      return messages !== undefined && messages.length > 0;
    };
  }

  get getMessageById() {
    return (messageId: string) =>
      Array.from(this._messages.entries())
        .find(([, messages]) => messages.find(m => m.id === messageId))?.[1]
        ?.find(m => m.id === messageId);
  }

  get messageCount() {
    return (variableSetIndex: number) => this._messages.get(variableSetIndex)?.length ?? 0;
  }

  @action
  setMessages(variableSetIndex: number, messages: ChatMessageI[]) {
    this._messages.set(variableSetIndex, messages);
  }

  @action
  addMessage(variableSetIndex: number, message: ChatMessageI) {
    const existing = this._messages.get(variableSetIndex) || [];
    // Dedup guard: skip if a message with this ID already exists.
    // This prevents duplicates from WebSocket retries, mutation onSuccess
    // firing after re-hydration, or any other double-add race condition.
    if (message.id && existing.some(m => m.id === message.id)) {
      return;
    }
    this._messages.set(variableSetIndex, [...existing, message]);
  }

  @action
  prependMessages(variableSetIndex: number, messages: ChatMessageI[]) {
    const existing = this._messages.get(variableSetIndex) || [];
    // Dedup guard: filter out any messages whose IDs already exist in the store.
    // This prevents duplicates when a page of older messages overlaps with
    // messages that arrived via WebSocket or optimistic updates since the
    // previous page was fetched.
    const existingIds = new Set(existing.map(m => m.id));
    const uniqueMessages = messages.filter(m => !m.id || !existingIds.has(m.id));
    if (uniqueMessages.length === 0) {
      return;
    }
    this._messages.set(variableSetIndex, [...uniqueMessages, ...existing]);
  }

  @action
  updateMessage(messageId: string, message: Partial<ChatMessageI>) {
    const entry = Array.from(this._messages.entries()).find(([, messages]) =>
      messages.some(m => m.id === messageId),
    );

    if (!entry) {
      return;
    }

    const [key, messages] = entry;

    this._messages.set(
      key,
      messages.map(msg => (msg.id === messageId ? { ...msg, ...message } : msg)),
    );
  }

  @action
  updateMessageStats(messageId: string, stats: ChatMessageStatsI) {
    const entry = Array.from(this._messages.entries()).find(([, messages]) =>
      messages.some(m => m.id === messageId),
    );

    if (!entry) {
      return;
    }

    const [key, messages] = entry;

    this._messages.set(
      key,
      messages.map(msg => (msg.id === messageId ? { ...msg, ...stats } : msg)),
    );

    const currentStats = this._stats.get(key) ?? { ...DEFAULT_STATS };
    this._stats.set(key, {
      total_messages: currentStats.total_messages,
      total_cost: currentStats.total_cost + (stats.cost ?? 0),
      total_input_tokens: currentStats.total_input_tokens + (stats.input_tokens ?? 0),
      total_output_tokens: currentStats.total_output_tokens + (stats.output_tokens ?? 0),
      total_latency_ms: currentStats.total_latency_ms + (stats.latency_ms ?? 0),
    });
  }

  @action
  appendToMessageContent(messageId: string, newContent: Content[]) {
    const entry = Array.from(this._messages.entries()).find(([, messages]) =>
      messages.some(m => m.id === messageId),
    );

    if (!entry) {
      return;
    }

    const [key, messages] = entry;

    this._messages.set(
      key,
      messages.map(msg =>
        msg.id === messageId
          ? { ...msg, content: [...(Array.isArray(msg.content) ? msg.content : []), ...newContent] }
          : msg,
      ),
    );
  }

  @action
  removeMessage(variableSetIndex: number, messageId: string) {
    const existing = this._messages.get(variableSetIndex);
    if (!existing) {
      return;
    }
    this._messages.set(
      variableSetIndex,
      existing.filter(m => m.id !== messageId),
    );
  }

  /**
   * Remove all messages that share the same `individual_run_request_id`.
   *
   * The backend stores a user message and its response(s) together in a
   * single `IndividualRunRequest` record.  Deleting any message from a turn
   * causes the backend to delete the entire record, so the frontend must
   * mirror that by removing every message that belongs to the same turn.
   *
   * Returns the number of messages removed so the caller can adjust stats.
   */
  @action
  removeMessagesByRequestId(variableSetIndex: number, requestId: number): number {
    const existing = this._messages.get(variableSetIndex);
    if (!existing) {
      return 0;
    }
    const remaining = existing.filter(m => m.individual_run_request_id !== requestId);
    const removedCount = existing.length - remaining.length;
    this._messages.set(variableSetIndex, remaining);
    return removedCount;
  }

  @action
  clearMessages(variableSetIndex?: number) {
    if (variableSetIndex !== undefined) {
      this._messages.delete(variableSetIndex);
      this.resetStats(variableSetIndex);
    } else {
      this._messages.clear();
      this._stats.clear();
    }
  }

  // ---------------------------------------------------------------------------
  // Pending Request (per variable set)
  // ---------------------------------------------------------------------------

  /**
   * Get the pending request ID for a specific variable set.
   */
  get getPendingRequestId() {
    return (variableSetIndex: number): number | null =>
      this._pending_request_ids.get(variableSetIndex) ?? null;
  }

  /**
   * @deprecated Use `getPendingRequestId(variableSetIndex)` instead.
   * Returns the first pending request ID found (for backward compatibility).
   */
  get pendingRequestId(): number | null {
    if (this._pending_request_ids.size === 0) {
      return null;
    }
    return this._pending_request_ids.values().next().value ?? null;
  }

  /**
   * Check if a specific variable set has a pending request.
   */
  get isVariableSetPending() {
    return (variableSetIndex: number): boolean => this._pending_request_ids.has(variableSetIndex);
  }

  /**
   * @deprecated Use `isVariableSetPending(variableSetIndex)` instead.
   * Returns true if ANY variable set has a pending request.
   */
  get isPending() {
    return this._pending_request_ids.size > 0;
  }

  /**
   * Check if a given request ID is one we're tracking.
   * Returns the variableSetIndex it belongs to, or null if not found.
   */
  get resolveRequest() {
    return (requestId: number): number | null =>
      this._request_to_variable_set.get(requestId) ?? null;
  }

  /**
   * @deprecated Use `resolveRequest(requestId)` instead.
   * Kept for backward compatibility — returns true if request belongs to any variable set.
   */
  get isOwnRequest() {
    return (requestId: number) => this._request_to_variable_set.has(requestId);
  }

  @action
  setPendingRequestId(variableSetIndex: number, requestId: number | null) {
    if (requestId === null) {
      // Clear the mapping when request completes
      const currentRequestId = this._pending_request_ids.get(variableSetIndex);
      if (currentRequestId !== undefined) {
        this._request_to_variable_set.delete(currentRequestId);
      }
      this._pending_request_ids.delete(variableSetIndex);
    } else {
      this._pending_request_ids.set(variableSetIndex, requestId);
      this._request_to_variable_set.set(requestId, variableSetIndex);
    }
  }

  /**
   * Clear pending state using only a request ID (when variable set index is not readily available).
   * This is useful for WebSocket handlers that resolve by request ID.
   */
  @action
  clearPendingByRequestId(requestId: number) {
    const variableSetIndex = this._request_to_variable_set.get(requestId);
    if (variableSetIndex !== undefined) {
      this._pending_request_ids.delete(variableSetIndex);
      this._request_to_variable_set.delete(requestId);
    }
  }

  // ---------------------------------------------------------------------------
  // Streaming (per variable set)
  // ---------------------------------------------------------------------------

  /**
   * Get the streaming message ID for a specific variable set.
   */
  get getStreamingMessageId() {
    return (variableSetIndex: number): string | null =>
      this._streaming_message_ids.get(variableSetIndex) ?? null;
  }

  /**
   * @deprecated Use `getStreamingMessageId(variableSetIndex)` instead.
   * Returns the first streaming message ID found (for backward compatibility).
   */
  get streamingMessageId(): string | null {
    if (this._streaming_message_ids.size === 0) {
      return null;
    }
    return this._streaming_message_ids.values().next().value ?? null;
  }

  /**
   * Check if a specific variable set is currently streaming.
   */
  get isVariableSetStreaming() {
    return (variableSetIndex: number): boolean => this._streaming_message_ids.has(variableSetIndex);
  }

  /**
   * True if any variable set is currently streaming.
   */
  get isAnyStreaming(): boolean {
    return this._streaming_message_ids.size > 0;
  }

  @action
  setStreamingMessageId(variableSetIndex: number, messageId: string | null) {
    if (messageId === null) {
      this._streaming_message_ids.delete(variableSetIndex);
    } else {
      this._streaming_message_ids.set(variableSetIndex, messageId);
    }
  }

  /**
   * Clear streaming state using only a request ID (resolves variable set internally).
   */
  @action
  clearStreamingByRequestId(requestId: number) {
    const variableSetIndex = this._request_to_variable_set.get(requestId);
    if (variableSetIndex !== undefined) {
      this._streaming_message_ids.delete(variableSetIndex);
    }
  }

  // ---------------------------------------------------------------------------
  // Socket Activity
  // ---------------------------------------------------------------------------

  get lastSocketActivity() {
    return this._last_socket_activity;
  }

  @action
  recordSocketActivity() {
    this._last_socket_activity = Date.now();
  }

  // ---------------------------------------------------------------------------
  // Stats (per variable set)
  // ---------------------------------------------------------------------------

  /**
   * Get stats for a specific variable set.
   */
  get getStats() {
    return (variableSetIndex: number): ChatStatsI =>
      this._stats.get(variableSetIndex) ?? { ...DEFAULT_STATS };
  }

  /**
   * @deprecated Use `getStats(variableSetIndex)` instead.
   * Returns aggregated stats across all variable sets.
   */
  get stats(): ChatStatsI {
    const aggregated: ChatStatsI = { ...DEFAULT_STATS };
    for (const s of this._stats.values()) {
      aggregated.total_messages += s.total_messages;
      aggregated.total_cost += s.total_cost;
      aggregated.total_input_tokens += s.total_input_tokens;
      aggregated.total_output_tokens += s.total_output_tokens;
      aggregated.total_latency_ms += s.total_latency_ms;
    }
    return aggregated;
  }

  @action
  setStats(variableSetIndex: number, stats: ChatStatsI) {
    this._stats.set(variableSetIndex, stats);
  }

  @action
  resetStats(variableSetIndex?: number) {
    if (variableSetIndex !== undefined) {
      this._stats.set(variableSetIndex, { ...DEFAULT_STATS });
    } else {
      this._stats.clear();
    }
  }

  @action
  incrementTotalMessages(variableSetIndex: number, count = 1) {
    const current = this._stats.get(variableSetIndex) ?? { ...DEFAULT_STATS };
    this._stats.set(variableSetIndex, {
      ...current,
      total_messages: current.total_messages + count,
    });
  }

  @action
  decrementTotalMessages(variableSetIndex: number, count = 1) {
    const current = this._stats.get(variableSetIndex) ?? { ...DEFAULT_STATS };
    this._stats.set(variableSetIndex, {
      ...current,
      total_messages: Math.max(0, current.total_messages - count),
    });
  }

  // ---------------------------------------------------------------------------
  // Rehydration
  // ---------------------------------------------------------------------------

  /**
   * Get the rehydration generation counter for a specific variable set.
   * useHydrateChatMessages watches this value and resets its
   * hasInitialized ref when it changes.
   */
  get getRehydrationGeneration() {
    return (variableSetIndex: number): number =>
      this._rehydration_generation.get(variableSetIndex) ?? 0;
  }

  /**
   * Clear messages for a variable set AND signal that the hydration hook
   * should re-fetch and re-initialise from the server.
   *
   * Use this instead of clearMessages when the server-side data has changed
   * (e.g. initial_chat_history was updated) and the UI must reflect it.
   */
  @action
  requestRehydration(variableSetIndex: number) {
    this._messages.delete(variableSetIndex);
    this.resetStats(variableSetIndex);
    const current = this._rehydration_generation.get(variableSetIndex) ?? 0;
    this._rehydration_generation.set(variableSetIndex, current + 1);
  }

  // ---------------------------------------------------------------------------
  // Request → Variable Set Mapping
  // ---------------------------------------------------------------------------

  /**
   * Register a mapping from requestId to variableSetIndex.
   * Called when sending a message, before the pending state is set.
   */
  @action
  registerRequest(requestId: number, variableSetIndex: number) {
    this._request_to_variable_set.set(requestId, variableSetIndex);
  }

  /**
   * Look up which variable set a request belongs to.
   */
  getVariableSetForRequest(requestId: number): number | null {
    return this._request_to_variable_set.get(requestId) ?? null;
  }

  // ---------------------------------------------------------------------------
  // Full Reset
  // ---------------------------------------------------------------------------

  @action
  reset() {
    this._messages.clear();
    this._pending_request_ids.clear();
    this._streaming_message_ids.clear();
    this._request_to_variable_set.clear();
    this._last_socket_activity = null;
    this._stats.clear();
    this._rehydration_generation.clear();
  }
}

export default ChatStore;
